import {
  GetInvoiceDetailDateInfo,
  GetInvoiceDetailItem,
  GetInvoiceDetailItemType,
  GetInvoiceDetailPricingAmount,
} from '../../types/plansales/invoice/invoiceDetail';
import { GroupPlanType } from '../../types/plansales/plan';
import { groupPlanMap } from '../../constants/plansales/billing';
import { getUtcDateDash } from '../utils/time';
import { getInvoiceSemanticsStatus } from './invoiceStatus';
import { NULL_TEXT } from '../../constants/common';
import { InvoiceDescriptionStatus } from 'types/plansales/billing';
import { GetInvoiceDetailResponse } from 'types/plansales/invoice/invoiceDetail';

/**
 * Invoice detail 에서 보이는 description 을 위한 module
 * closet-next 과 싱크 맞춤.
 */
interface UseInvoiceSummary {
  summaryMessage: string;
  additionalMessage?: string;
}

interface Props {
  groupName: string;
  amount: GetInvoiceDetailPricingAmount;
  detail: GetInvoiceDetailDateInfo;
  items: GetInvoiceDetailItem[];
  planType: GroupPlanType;
}

export const useInvoiceSummary = ({
  groupName,
  planType,
  detail,
  items,
  amount,
}: Props): UseInvoiceSummary => {
  const planName = groupPlanMap[planType];
  const paidDate = detail.succeedDate ? getUtcDateDash(detail.succeedDate) : '';
  const refundedDate = detail.refundDate ? getUtcDateDash(detail.refundDate) : '';

  const planEndDate = items?.[0]?.endDate;
  const status = getInvoiceSemanticsStatus(planType, items, detail, amount.total);
  const totalAmount = Math.abs(amount.total).toFixed(2); // TODO: (기획 중) 추후 환종 단위 붙이는 유틸 적용

  const credit =
    items.find(item => item.type === GetInvoiceDetailItemType.CARRY_OVER_BALANCE)?.amount || 0;
  const creditAmount = Math.abs(credit).toFixed(2); // TODO: (기획 중) 추후 환종 단위 붙이는 유틸 적용
  //
  switch (status) {
    case InvoiceDescriptionStatus.START_NOT_YET_PAID:
      return {
        summaryMessage: `Your Group, ${groupName}, has started the ${planName} Plan. The plan will be renewed every month.`,
      };
    case InvoiceDescriptionStatus.RENEW_NOT_YET_PAID:
      return {
        summaryMessage: `Your Group, ${groupName}, has renewed the ${planName} Plan. The plan will be renewed every month.`,
      };
    case InvoiceDescriptionStatus.CLOSE_PLAN_AND_NOT_YET_PAID:
    case InvoiceDescriptionStatus.DOWNGRADE_WITH_MINUS_CREDIT:
      return {
        summaryMessage: `Your Group has settled the Addition Credits that remained.`,
        additionalMessage: `You have completed the payment for ${totalAmount} by card on ${planEndDate}.`,
      };
    case InvoiceDescriptionStatus.START_PAID_BY_CARD_WITH_DEBT:
    case InvoiceDescriptionStatus.START_PAID_BY_ONLY_CARD:
      return {
        summaryMessage: `Your Group, ${groupName}, has started the ${planName} Plan. The plan will be renewed every month.`,
        additionalMessage: `You have completed the payment for ${totalAmount} by card on ${paidDate}.`,
      };
    case InvoiceDescriptionStatus.RENEW_PAID_BY_CARD_WITH_DEBT:
    case InvoiceDescriptionStatus.RENEW_PAID_BY_ONLY_CARD:
      return {
        summaryMessage: `Your Group, ${groupName}, has renewed the ${planName} Plan. The plan will be renewed every month.`,
        additionalMessage: `You have completed the payment for ${totalAmount} by card on ${paidDate}.`,
      };
    case InvoiceDescriptionStatus.START_PAID_BY_ONLY_CREDIT:
      return {
        summaryMessage: `Your Group, ${groupName}, has started the ${planName} Plan. The plan will be renewed every month.`,
        additionalMessage: `You have completed the payment for ${creditAmount} by Deduction Credits on ${paidDate}. `,
      };
    case InvoiceDescriptionStatus.RENEW_PAID_BY_ONLY_CREDIT:
      return {
        summaryMessage: `Your Group, ${groupName}, has renewed the ${planName} Plan. The plan will be renewed every month.`,
        additionalMessage: `You have completed the payment for ${creditAmount} by Deduction Credits on ${paidDate}. `,
      };
    case InvoiceDescriptionStatus.RENEW_PAID_BY_MULTI_METHOD: {
      return {
        summaryMessage: `Your Group, ${groupName}, has renewed the ${planName} Plan. The plan will be renewed every month.`,
        additionalMessage: `You have completed the payment using ${creditAmount} of Deduction Credits and ${totalAmount} paid by card on ${paidDate}.`,
      };
    }
    case InvoiceDescriptionStatus.START_PAID_BY_MULTI_METHOD: {
      return {
        summaryMessage: `Your Group, ${groupName}, has started the ${planName} Plan. The plan will be renewed every month.`,
        additionalMessage: `You have completed the payment using ${creditAmount} of Deduction Credits and ${totalAmount} paid by card on ${paidDate}.`,
      };
    }
    // 리뉴얼 전 스크립트를 그대로 사용
    case InvoiceDescriptionStatus.REFUND_RENEW_PAID_BY_MULTI_METHOD:
    case InvoiceDescriptionStatus.REFUND_RENEW_PAID_BY_ONLY_CARD:
    case InvoiceDescriptionStatus.REFUND_RENEW_PAID_BY_ONLY_CREDIT:
    case InvoiceDescriptionStatus.REFUND_START_PAID_BY_ONLY_CARD:
    case InvoiceDescriptionStatus.REFUND_START_PAID_BY_MULTI_METHOD:
    case InvoiceDescriptionStatus.REFUND_START_PAID_BY_ONLY_CREDIT:
      return {
        summaryMessage: `The payment that you've completed before has been canceled on ${refundedDate}`,
      };

    case InvoiceDescriptionStatus.NO_CASE:
      return {
        summaryMessage: NULL_TEXT, // 미처 대응하지 못한 케이스 이므로 베니를 호출해주세요.
      };
    default:
      return { summaryMessage: NULL_TEXT }; // 미처 대응하지 못한 케이스 이므로 베니를 호출해주세요.
  }

  return {
    summaryMessage: 'Error', // 여기 까지 떨어지는 경우 switch 돌지도 못한 에러케이스
  };
};

export const useInvoiceDetailPaymentInfo = (
  data: GetInvoiceDetailResponse,
): {
  cardAmount: string;
  creditAmount: string;
  totalBilledAmount: string;
  isPending: boolean;
  isRefunded: boolean;
} => {
  // 크레딧으로 결제 했다면 groupInvoiceItems 에 CARRY_OVER_BALANCE type 의 아이템이 들어있슴다.
  const creditItem = data.groupInvoiceItems.find(
    item => item.type === GetInvoiceDetailItemType.CARRY_OVER_BALANCE,
  );

  const creditPrice = creditItem ? creditItem.amount : 0;
  const cardTotalPrice = data.amount.total; // total 에는 항상 카드 결제 금액이 들어온다. 즉 우리가 생각하는 전체 제품 가격이 아니다.
  const totalPrice = cardTotalPrice + creditPrice;

  const isRefunded = !!data.detail.refundDate;
  const isPending = !!data.detail.succeedDate;

  const currency = '$ '; // TODO: ted 께서 주시는 api 에서 통화단위를 받아온다.

  return {
    cardAmount: isPending ? NULL_TEXT : currency + cardTotalPrice.toFixed(2),
    creditAmount: isPending ? NULL_TEXT : currency + creditPrice.toFixed(2),
    totalBilledAmount: currency + totalPrice.toFixed(2),
    isRefunded,
    isPending,
  };
};
